"use client";

import useSearchRouter from "@/app/hooks/useSearchRouter";
import Pill from "@/components/design-system/pill";
import SearchFeedback from "@/components/search-feedback/searchFeedback";
import SearchFeedbackWithChat from "@/components/search-feedback/searchFeedbackWithChat";
import XIcon from "public/next-public/common/icons/x-close-icon.svg";
import styles from "./noResultsSection.module.scss";

type NoResultsBoundaryServerProps = {
  query: string | undefined;
  pathname?: string;
};

const NoResultsSectionServer = ({ query, pathname }: NoResultsBoundaryServerProps) => {
  const { removeCustomFacet, isCustomFacetActive } = useSearchRouter();
  const showingPurchasedBefore = isCustomFacetActive("purchasedBefore");
  const showingFreeShipping = false;
  const showingFastDelivery = false;
  const showingAuthorizedVendor = false;

  const handleClearFilter = (filterName: string) => {
    console.log(`Clear filter: ${filterName}`);
    removeCustomFacet(filterName);
    // You can add real logic here to update the router or filters
  };

  const showAnyFilter = showingPurchasedBefore || showingFreeShipping || showingFastDelivery || showingAuthorizedVendor;

  if (showAnyFilter) {
    return (
      <div className="ms-auto col-12">
        <section className="flex-grow-1">
          <section className="mb-3">
            <div className={styles.customCard}>
              <div className={styles.textGroup}>
                <span className={styles.noresults}>There are no items to show based on your current filters.</span>
                <span className={styles.noResultSuggestions}>
                  Try clearing these filters to get back to a list of results.
                </span>
              </div>
              <div className="d-flex justify-content-center gap-2 flex-wrap">
                {showingPurchasedBefore && (
                  <Pill
                    size="medium"
                    label="Purchased Before"
                    selected
                    trailingIcon={<XIcon width={16} height={16} aria-hidden />}
                    ariaLabel="Remove Purchased Before filter"
                    onClick={() => handleClearFilter("purchasedBefore")}
                  />
                )}
                {showingFreeShipping && (
                  <Pill
                    size="medium"
                    label="Free Shipping"
                    selected
                    trailingIcon={<XIcon width={16} height={16} aria-hidden />}
                    ariaLabel="Remove Free Shipping filter"
                    onClick={() => handleClearFilter("Free Shipping")}
                  />
                )}
                {showingFastDelivery && (
                  <Pill
                    size="medium"
                    label="Fast Delivery"
                    selected
                    trailingIcon={<XIcon width={16} height={16} aria-hidden />}
                    ariaLabel="Remove Fast Delivery filter"
                    onClick={() => handleClearFilter("Fast Delivery")}
                  />
                )}
                {showingAuthorizedVendor && (
                  <Pill
                    size="medium"
                    label="Authorized Vendor"
                    selected
                    trailingIcon={<XIcon width={16} height={16} aria-hidden />}
                    ariaLabel="Remove Authorized Vendor filter"
                    onClick={() => handleClearFilter("Authorized Vendor")}
                  />
                )}
              </div>
            </div>
          </section>

          <section className="mb-3">
            {pathname === "/search" ? <SearchFeedbackWithChat /> : <SearchFeedback query={query} />}
          </section>
        </section>
      </div>
    );
  }

  return (
    <div className="ms-auto col-12">
      <section className="flex-grow-1">
        <section className="mb-3">
          <div className={`${styles.customCard} ${styles.searchEmpty}`}>
            {query ? (
              <p>
                Sorry, we couldn&apos;t find any products matching{" "}
                <span className={styles.queryText}>{`"${query}."`}</span>
              </p>
            ) : (
              <p>No results found</p>
            )}
            <p>
              Try a broader product name, brand, or manufacturer. If you&apos;re still not seeing results, use a broader
              term or shorten your search.
            </p>
          </div>
        </section>
        <section className="mb-3">
          <SearchFeedbackWithChat />
        </section>
      </section>
    </div>
  );
};

export default NoResultsSectionServer;
